import React, { useState, useEffect, useRef } from "react";
import { CrudFilter, useList } from "@refinedev/core";
import {
  Box,
  Input,
  InputGroup,
  InputRightElement,
  FormControl,
  FormLabel,
  FormErrorMessage,
  List,
  ListItem,
  Text,
  Spinner,
  VStack,
  HStack,
  Tag,
  TagLabel,
  TagCloseButton,
  Flex,
  useColorModeValue,
} from "@chakra-ui/react";
import { ChevronDown, ChevronUp, X } from "lucide-react";

interface Option {
  id?: string | number; // Made optional
  label: string;
  value: string;
}

interface MultipleAutoCompleteProps {
  source?: string; // Made optional since we can use static options
  label?: string;
  placeholder?: string;
  value?: string[]; // Always array for multiple selection
  onChange: (value: string[]) => void; // Always returns array
  displayField?: string; // Field to display in options (default: 'name')
  valueField?: string; // Field to use as value (default: 'id')
  filters?: CrudFilter[]; // Additional filters for the API
  debounceMs?: number; // Debounce delay for search (default: 300ms)
  minChars?: number; // Minimum characters before search (default: 1)
  maxResults?: number; // Maximum results to show (default: 10)
  disabled?: boolean;
  required?: boolean;
  error?: string;
  className?: string;
  maxSelections?: number; // Maximum number of selections allowed
  sortBy?: {
    field: string;
    order: "asc" | "desc";
  };
  options?: Option[]; // Static options array
}

export const MultipleAutoComplete: React.FC<MultipleAutoCompleteProps> = ({
  source,
  label,
  placeholder,
  value = [],
  onChange,
  displayField = "name",
  valueField = "id",
  filters = [],
  debounceMs = 300,
  minChars = 1,
  maxResults = 10,
  disabled = false,
  required = false,
  error,
  className = "",
  maxSelections,
  sortBy,
  options: staticOptions,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [selectedOptions, setSelectedOptions] = useState<Option[]>([]);

  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Determine if we should use static options or fetch from API
  const useStaticOptions = staticOptions && staticOptions.length > 0;

  // Chakra UI color mode values
  const boxBg = useColorModeValue("gray.100", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const hoverBg = useColorModeValue("gray.50", "gray.700");
  const highlightBg = useColorModeValue("blue.50", "blue.900");
  const highlightColor = useColorModeValue("blue.900", "blue.100");
  const textColor = useColorModeValue("gray.900", "white");
  const placeholderColor = useColorModeValue("gray.500", "gray.400");
  const tagBg = useColorModeValue("blue.100", "blue.800");
  const tagColor = useColorModeValue("blue.800", "blue.100");

  // Only use useList hook when not using static options and source is provided
  const { data, isLoading, isError } = useList({
    resource: source || "",
    filters: searchTerm
      ? [
          ...filters,
          {
            field: "search",
            operator: "eq",
            value: searchTerm,
          },
        ]
      : [],
    sorters: sortBy ? [{ field: sortBy.field, order: sortBy.order }] : [],
    pagination: {
      pageSize: maxResults,
      current: 1,
    },
    queryOptions: {
      enabled:
        !useStaticOptions &&
        !!source &&
        (searchTerm.length >= minChars || searchTerm.length === 0),
    },
  });

  // Transform data to options format or use static options
  const options: Option[] = useStaticOptions
    ? staticOptions
    : data?.data?.map((item: any) => ({
        id: item[valueField],
        label: item[displayField],
        value: item[valueField].toString(),
      })) || [];

  // Filter options based on search term when using static options
  const filteredBySearch =
    useStaticOptions && searchTerm.length >= minChars
      ? options
          .filter((option) =>
            option.label.toLowerCase().includes(searchTerm.toLowerCase())
          )
          .slice(0, maxResults)
      : options;

  // Filter out already selected options
  const filteredOptions = filteredBySearch.filter(
    (option) =>
      !selectedOptions.some((selected) => selected.value === option.value)
  );

  // Debounced search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm.length >= minChars && isOpen) {
        // For static options, filtering happens in render
        // For API options, this triggers the useList hook
      }
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [searchTerm, debounceMs, minChars, isOpen]);

  const hasRunRef = useRef(false);

  useEffect(() => {
    if (
      !hasRunRef.current &&
      value &&
      value.length > 0 &&
      options &&
      options.length > 0
    ) {
      const getOptions = value
        .map((val) => options.find((opt) => opt.value === val))
        .filter((opt): opt is Option => opt !== undefined);

      const isAlreadySelected = getOptions.every((opt) =>
        selectedOptions.some((sel) => sel.value === opt.value)
      );

      if (!isAlreadySelected) {
        setSelectedOptions((prev) => [...prev, ...getOptions]);
        onChange(getOptions.map((opt) => opt.value));
      }

      // Mark that this effect has run
      hasRunRef.current = true;
    }
  }, [value, options]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
    setIsOpen(true);
    setHighlightedIndex(-1);
  };

  const handleOptionSelect = (option: Option) => {
    // Check if maximum selections reached
    if (maxSelections && selectedOptions.length >= maxSelections) {
      return;
    }

    const newSelectedOptions = [...selectedOptions, option];
    setSelectedOptions(newSelectedOptions);
    setSearchTerm("");
    setHighlightedIndex(-1);
    onChange(newSelectedOptions.map((opt) => opt.value));
  };

  const handleRemoveSelection = (optionToRemove: Option) => {
    const newSelectedOptions = selectedOptions.filter(
      (opt) => opt.value !== optionToRemove.value
    );
    setSelectedOptions(newSelectedOptions);
    onChange(newSelectedOptions.map((opt) => opt.value));
  };

  const handleClearAll = () => {
    setSelectedOptions([]);
    setSearchTerm("");
    onChange([]);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === "ArrowDown" || e.key === "Enter") {
        setIsOpen(true);
        return;
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < filteredOptions.length - 1 ? prev + 1 : 0
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev > 0 ? prev - 1 : filteredOptions.length - 1
        );
        break;
      case "Enter":
        e.preventDefault();
        if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
          handleOptionSelect(filteredOptions[highlightedIndex]);
        }
        break;
      case "Escape":
        setIsOpen(false);
        setHighlightedIndex(-1);
        inputRef.current?.blur();
        break;
      case "Backspace":
        if (!searchTerm && selectedOptions.length > 0) {
          e.preventDefault();
          handleRemoveSelection(selectedOptions[selectedOptions.length - 1]);
        }
        break;
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getPlaceholderText = () => {
    if (selectedOptions.length > 0) {
      return ``;
    }
    return placeholder;
  };

  const showClearButton = selectedOptions.length > 0;

  // Determine loading and error states
  const isLoadingState = useStaticOptions ? false : isLoading;
  const isErrorState = useStaticOptions ? false : isError;

  return (
    <Box
      ref={containerRef}
      position="relative"
      className={className}
      //   maxW={"500px"}
    >
      <FormControl isInvalid={!!error} isRequired={required}>
        {/* Label */}
        {label && (
          <FormLabel fontSize="sm" fontWeight="medium">
            {label}
          </FormLabel>
        )}

        {/* Custom "input field" container */}
        <Flex
          alignItems="center"
          flexWrap="wrap"
          border="1px solid"
          borderColor={error ? "red.500" : "gray.300"}
          borderRadius="md"
          py={2}
          px={3}
          bg="white"
          _focusWithin={{
            borderColor: "blue.500",
            boxShadow: "0 0 0 1px #3182ce",
          }}
        >
          {/* Tags */}
          {selectedOptions.map((option) => (
            <Tag
              key={option.id || option.value}
              size="sm"
              bg={tagBg}
              color={tagColor}
              borderRadius="full"
              mr={1}
              mb={1}
            >
              <TagLabel>{option.label}</TagLabel>
              <TagCloseButton
                onClick={() => handleRemoveSelection(option)}
                disabled={disabled}
              />
            </Tag>
          ))}

          {/* Input */}
          <Input
            ref={inputRef}
            placeholder={getPlaceholderText()}
            value={searchTerm}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => setIsOpen(true)}
            disabled={disabled}
            variant="unstyled"
            color={textColor}
            _placeholder={{ color: placeholderColor }}
            flex="1"
            minW="120px"
            autoComplete="off"
          />

          {/* Icon inside input field */}
          <Box ml="auto" pr={1} pointerEvents="none">
            {isOpen ? (
              <ChevronUp size={16} color="#878787" />
            ) : (
              <ChevronDown size={16} color="#878787" />
            )}
          </Box>
        </Flex>

        {/* Error message */}
        {error && <FormErrorMessage>{error}</FormErrorMessage>}
      </FormControl>

      {/* Options dropdown */}
      {isOpen && (
        <Box
          position="absolute"
          top="100%"
          left={0}
          right={0}
          zIndex={50}
          mt={1}
          bg={"white"}
          border="1px solid"
          borderColor={borderColor}
          borderRadius="xl"
          boxShadow="lg"
          maxH="240px"
          overflowY="auto"
        >
          {isErrorState ? (
            <Box px={4} py={3}>
              <Text fontSize="sm" color="red.500">
                Error loading options
              </Text>
            </Box>
          ) : isLoadingState ? (
            <Box px={4} py={3}>
              <VStack spacing={2}>
                <Spinner size="sm" />
                <Text fontSize="sm" color={placeholderColor}>
                  Loading...
                </Text>
              </VStack>
            </Box>
          ) : filteredOptions.length === 0 ? (
            <Box px={4} py={3}>
              <Text fontSize="sm" color={placeholderColor}>
                {searchTerm.length < minChars
                  ? `Type at least ${minChars} character${
                      minChars > 1 ? "s" : ""
                    } to search`
                  : selectedOptions.length === options.length
                  ? "All options selected"
                  : "No options found"}
              </Text>
            </Box>
          ) : (
            <List spacing={0} py={1}>
              {filteredOptions.map((option, index) => (
                <ListItem
                  key={option.id || option.value}
                  px={4}
                  py={3}
                  cursor="pointer"
                  fontSize="sm"
                  color={
                    index === highlightedIndex ? highlightColor : textColor
                  }
                  bg={index === highlightedIndex ? highlightBg : "transparent"}
                  _hover={{ bg: hoverBg }}
                  onClick={() => handleOptionSelect(option)}
                >
                  {option.label}
                </ListItem>
              ))}
              {maxSelections && selectedOptions.length >= maxSelections && (
                <Box
                  px={4}
                  py={2}
                  borderTop="1px solid"
                  borderColor={borderColor}
                >
                  <Text fontSize="xs" color={placeholderColor}>
                    Maximum {maxSelections} selections allowed
                  </Text>
                </Box>
              )}
            </List>
          )}
        </Box>
      )}
    </Box>
  );
};
